import { useState, useEffect } from "react";
import { Row, Col, Button, Card, Container, Badge, Carousel } from "react-bootstrap";
import { Link } from "react-router-dom";
import { API_ENDPOINTS, apiCall } from "../config/api";

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    // Fetch featured products (first 3 active products)
    apiCall(API_ENDPOINTS.PRODUCTS.ACTIVE)
      .then((data) => {
        setFeaturedProducts(data.slice(0, 3));
      })
      .catch((error) => {
        console.error("Error fetching featured products:", error);
      });
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section bg-primary text-white py-5 mb-5">
        <Container>
          <Row className="align-items-center min-vh-50">
            <Col lg={6}>
              <h1 className="display-4 fw-bold mb-4">
                Welcome to <span className="text-warning">Kompyuteran Atbp.</span>
              </h1>
              <p className="lead mb-4">
                Your trusted partner for all computer needs. From laptops to accessories,
                we provide quality tech solutions at unbeatable prices.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <Button
                  variant="warning"
                  size="lg"
                  as={Link}
                  to="/products"
                  className="px-4 py-2"
                >
                  🛒 Shop Now
                </Button>
                <Button
                  variant="outline-light"
                  size="lg"
                  as={Link}
                  to="/products"
                  className="px-4 py-2"
                >
                  Browse Products
                </Button>
              </div>
            </Col>
            <Col lg={6} className="text-center">
              <div className="hero-image-placeholder bg-light rounded p-5 text-dark">
                <h3>💻 Tech Paradise</h3>
                <p>Discover amazing deals on computers, laptops, and accessories!</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <Container>
        {/* Features Section */}
        <section className="features-section mb-5">
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Why Choose Us?</h2>
              <p className="lead text-muted">We're committed to providing the best tech shopping experience</p>
            </Col>
          </Row>
          <Row>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div className="feature-icon mb-3">
                    <span style={{ fontSize: '3rem' }}>🚚</span>
                  </div>
                  <Card.Title>Fast Delivery</Card.Title>
                  <Card.Text className="text-muted">
                    Quick and reliable shipping to get your tech gear to you fast.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div className="feature-icon mb-3">
                    <span style={{ fontSize: '3rem' }}>💰</span>
                  </div>
                  <Card.Title>Best Prices</Card.Title>
                  <Card.Text className="text-muted">
                    Competitive pricing on all computer products and accessories.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div className="feature-icon mb-3">
                    <span style={{ fontSize: '3rem' }}>🛡️</span>
                  </div>
                  <Card.Title>Quality Guarantee</Card.Title>
                  <Card.Text className="text-muted">
                    All products come with warranty and quality assurance.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>

        {/* Featured Products Section */}
        <section className="featured-products-section mb-5">
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Featured Products</h2>
              <p className="lead text-muted">Check out our most popular items</p>
            </Col>
          </Row>
          <Row>
            {featuredProducts.length > 0 ? (
              featuredProducts.map((product, index) => (
                <Col md={4} key={product._id} className="mb-4">
                  <Card className="h-100 shadow-sm border-0 product-card">
                    <div className="position-relative">
                      <Card.Img
                        variant="top"
                        src="https://via.placeholder.com/300x200/007bff/ffffff?text=Tech+Product"
                        alt={product.name}
                        style={{ height: '200px', objectFit: 'cover' }}
                      />
                      <Badge
                        bg="success"
                        className="position-absolute top-0 start-0 m-2"
                      >
                        Featured
                      </Badge>
                    </div>
                    <Card.Body className="d-flex flex-column">
                      <Card.Title className="h5">{product.name}</Card.Title>
                      <Card.Text className="text-muted flex-grow-1">
                        {product.description}
                      </Card.Text>
                      <div className="d-flex justify-content-between align-items-center mt-auto">
                        <span className="h5 text-primary mb-0">₱{product.price}</span>
                        <Button
                          variant="primary"
                          as={Link}
                          to={`/productView/${product._id}`}
                          size="sm"
                        >
                          View Details
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            ) : (
              // Placeholder cards when no products are loaded
              [1, 2, 3].map((index) => (
                <Col md={4} key={index} className="mb-4">
                  <Card className="h-100 shadow-sm border-0">
                    <Card.Img
                      variant="top"
                      src="https://via.placeholder.com/300x200/6c757d/ffffff?text=Loading..."
                      style={{ height: '200px', objectFit: 'cover' }}
                    />
                    <Card.Body className="d-flex flex-column">
                      <Card.Title>Loading Products...</Card.Title>
                      <Card.Text className="text-muted flex-grow-1">
                        Please wait while we load our featured products.
                      </Card.Text>
                      <Button variant="secondary" disabled>
                        Loading...
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
          <Row className="text-center mt-4">
            <Col>
              <Button
                variant="outline-primary"
                size="lg"
                as={Link}
                to="/products"
                className="px-5"
              >
                View All Products →
              </Button>
            </Col>
          </Row>
        </section>

        {/* Categories Section */}
        <section className="categories-section mb-5">
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Shop by Category</h2>
              <p className="lead text-muted">Find exactly what you're looking for</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="mb-4">
              <Card className="category-card h-100 border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div className="category-icon mb-3">
                    <span style={{ fontSize: '4rem' }}>💻</span>
                  </div>
                  <Card.Title>Laptops</Card.Title>
                  <Card.Text className="text-muted">
                    Gaming, business, and everyday laptops
                  </Card.Text>
                  <Button variant="outline-primary" as={Link} to="/products">
                    Browse Laptops
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="category-card h-100 border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div className="category-icon mb-3">
                    <span style={{ fontSize: '4rem' }}>🖥️</span>
                  </div>
                  <Card.Title>Desktops</Card.Title>
                  <Card.Text className="text-muted">
                    Custom builds and pre-built systems
                  </Card.Text>
                  <Button variant="outline-primary" as={Link} to="/products">
                    Browse Desktops
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="category-card h-100 border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div className="category-icon mb-3">
                    <span style={{ fontSize: '4rem' }}>🖱️</span>
                  </div>
                  <Card.Title>Accessories</Card.Title>
                  <Card.Text className="text-muted">
                    Keyboards, mice, and peripherals
                  </Card.Text>
                  <Button variant="outline-primary" as={Link} to="/products">
                    Browse Accessories
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="category-card h-100 border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div className="category-icon mb-3">
                    <span style={{ fontSize: '4rem' }}>🔧</span>
                  </div>
                  <Card.Title>Components</Card.Title>
                  <Card.Text className="text-muted">
                    CPUs, GPUs, RAM, and more
                  </Card.Text>
                  <Button variant="outline-primary" as={Link} to="/products">
                    Browse Components
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>

        {/* Testimonials Section */}
        <section className="testimonials-section mb-5">
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">What Our Customers Say</h2>
              <p className="lead text-muted">Don't just take our word for it</p>
            </Col>
          </Row>
          <Row>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="mb-3">
                    <span style={{ fontSize: '2rem', color: '#ffc107' }}>⭐⭐⭐⭐⭐</span>
                  </div>
                  <Card.Text className="fst-italic mb-3">
                    "Amazing service and great prices! Got my gaming laptop here and couldn't be happier."
                  </Card.Text>
                  <Card.Title className="h6">- Maria Santos</Card.Title>
                  <Card.Text className="text-muted small">Verified Customer</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="mb-3">
                    <span style={{ fontSize: '2rem', color: '#ffc107' }}>⭐⭐⭐⭐⭐</span>
                  </div>
                  <Card.Text className="fst-italic mb-3">
                    "Fast delivery and excellent customer support. Highly recommended!"
                  </Card.Text>
                  <Card.Title className="h6">- Juan Dela Cruz</Card.Title>
                  <Card.Text className="text-muted small">Verified Customer</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="mb-3">
                    <span style={{ fontSize: '2rem', color: '#ffc107' }}>⭐⭐⭐⭐⭐</span>
                  </div>
                  <Card.Text className="fst-italic mb-3">
                    "Best computer store in town. Quality products at affordable prices."
                  </Card.Text>
                  <Card.Title className="h6">- Anna Reyes</Card.Title>
                  <Card.Text className="text-muted small">Verified Customer</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>
      </Container>

      {/* Call-to-Action Section */}
      <section className="cta-section bg-dark text-white py-5">
        <Container>
          <Row className="text-center">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Ready to Upgrade Your Tech?</h2>
              <p className="lead mb-4">
                Join thousands of satisfied customers who trust Kompyuteran Atbp. for their tech needs.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button
                  variant="warning"
                  size="lg"
                  as={Link}
                  to="/products"
                  className="px-5 py-3"
                >
                  🛒 Start Shopping
                </Button>
                <Button
                  variant="outline-light"
                  size="lg"
                  as={Link}
                  to="/register"
                  className="px-5 py-3"
                >
                  Create Account
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section bg-light py-5">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h3 className="fw-bold mb-3">Stay Updated with Latest Deals</h3>
              <p className="text-muted mb-4">
                Subscribe to our newsletter and be the first to know about new products and exclusive offers.
              </p>
              <div className="d-flex gap-2 justify-content-center">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Enter your email address"
                  style={{ maxWidth: '300px' }}
                />
                <Button variant="primary">Subscribe</Button>
              </div>
              <p className="small text-muted mt-2">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}
